import { NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useParams } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canCreateBusinessClientUser, canRead } from 'features/Users/utils'
import {
  BusinessClientUsersTabs,
  EBusinessClientUserTab
} from 'features/BusinessClients/components/BusinessClientUsersTabs'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { getShortId } from 'utils/string'
import { GET_BUSINESS_CLIENT_USERS } from 'features/BusinessClientUsers/queries'
import EmptyState from 'components/v2/EmptyState'
import { getQueries } from 'utils/route'
import { BusinessClientUsersList } from 'features/BusinessClientUsers/components/BusinessClientUsersList/List'
import { isEmpty } from 'lodash'
import { PaginationList } from 'components/PaginationList'

const LIMIT = 30
const messages = defineMessages({
  helmet: {
    id: 'businessClientUsers.helmet',
    defaultMessage: 'Business Client Users | Nexbus Admin'
  }
})

const BusinessClientUsersView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { policies } = login.accessToken
  const { businessClientId } = useParams()
  const queries = getQueries(location.search)
  const { page = '1' } = queries
  const pageNb = parseInt(page)
  const businessClient = useBusinessClientAutocomplete(businessClientId as string)
  const {
    data: businessClientUsersData,
    loading: businessClientUsersLoading,
    error: businessClientUsersError,
    networkStatus: businessClientUsersNetworkStatus,
    refetch: businessClientUsersRefetch
  } = useQuery(GET_BUSINESS_CLIENT_USERS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        size: LIMIT,
        page: pageNb
      },
      input: {
        businessClientID: businessClientId
      }
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.BUSINESS_CLIENT_USER, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT_USER} />
  }

  let content: ReactElement

  if (businessClientUsersLoading || businessClientUsersNetworkStatus === NetworkStatus.refetch) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (businessClientUsersError) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="businessClients.users.error" defaultMessage="Error loading business client users" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            kind="primary"
            onClick={(): void => {
              businessClientUsersRefetch()
            }}
          >
            <FormattedMessage id="common.retry" defaultMessage="Retry" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(businessClientUsersData?.businessClientUsers.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessClients.users.empty"
            defaultMessage="There are no users for this business client"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to={`/business-clients/${businessClientId}/users/new`} kind="primary">
            <FormattedMessage id="businessClients.users.empty.action" defaultMessage="Add a user" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <BusinessClientUsersList businessClientUsers={businessClientUsersData?.businessClientUsers.items || []} />
        <PaginationList
          page={pageNb}
          limit={LIMIT}
          totalItems={businessClientUsersData?.businessClientUsers.total || 0}
          currentItemsCount={businessClientUsersData?.businessClientUsers.items.length || 0}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage
            id="businessClients.header.label"
            defaultMessage="{businessClientName} {businessClientId}"
            values={{
              businessClientName: businessClient?.name,
              businessClientId: getShortId(businessClientId as string).toUpperCase()
            }}
          />
        </Heading.H3>
        {canCreateBusinessClientUser(policies) && (
          <Button as={NavLink} to={`/business-clients/${businessClientId}/users/new`} kind="primary">
            <FormattedMessage id="companyClients.new.btn" defaultMessage="+ Add User" />
          </Button>
        )}
      </ContentHeader>
      <BusinessClientUsersTabs activeTab={EBusinessClientUserTab.Users} />
      <ContentBody withTabs>{content}</ContentBody>
    </Content>
  )
}

export default withLastUrl(BusinessClientUsersView)
